import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { handleError, handleSuccess } from '../../utils';
import { ToastContainer } from 'react-toastify';
import {
    Container,
    Typography,
    TextField,
    Button,
    Box,
    Stack,
    FormControlLabel,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    Assignment as TemplateIcon
} from '@mui/icons-material';

const ProgramTemplateCreate = () => {
    const [formData, setFormData] = useState({
        name: '',
        ageRange: []
    });
    const [loading, setLoading] = useState(false);
    
    // Age range states
    const [ageType, setAgeType] = useState('single');
    const [singleAge, setSingleAge] = useState('');
    const [ageFrom, setAgeFrom] = useState('');
    const [ageTo, setAgeTo] = useState('');

    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    const axiosConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    };

    // Common TextField styling
    const textFieldStyles = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            '& fieldset': {
                borderRadius: 3,
            },
            '&:hover fieldset': {
                borderColor: '#673AB7',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#673AB7',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#673AB7',
        },
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAgeTypeChange = (event) => {
        setAgeType(event.target.value);
        setSingleAge('');
        setAgeFrom('');
        setAgeTo('');
        setFormData(prev => ({
            ...prev,
            ageRange: []
        }));
    };

    const generateAgeRange = () => {
        let ages = [];
        if (ageType === 'single') {
            if (singleAge) {
                ages = [{ age: parseInt(singleAge) }];
            }
        } else {
            if (ageFrom && ageTo) {
                const from = parseInt(ageFrom);
                const to = parseInt(ageTo);
                if (from <= to) {
                    for (let i = from; i <= to; i++) {
                        ages.push({ age: i });
                    }
                }
            }
        }
        setFormData(prev => ({
            ...prev,
            ageRange: ages
        }));
    };

    React.useEffect(() => {
        generateAgeRange();
    }, [ageType, singleAge, ageFrom, ageTo]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name) {
            handleError('Program name is required');
            return;
        }

        try {
            setLoading(true);
            const url = `${API_URL}/api/program-templates`;
            const response = await axios.post(url, formData, axiosConfig);
            const result = response.data;

            if (result.success) {
                handleSuccess('Program template created successfully');
                setTimeout(() => {
                    navigate('/program-templates');
                }, 1000);
            } else {
                handleError(result.message || 'Failed to create template');
            }
        } catch (error) {
            console.error('Error creating template:', error);
            if (error.response) {
                const errorMessage = error.response.data?.message || 'Failed to create template';
                handleError(errorMessage);
            } else {
                handleError('Network error - please check your connection');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box component="form" onSubmit={handleSubmit}>
                {/* Title */}
                <Box display="flex" alignItems="center" mb={4}>
                    <TemplateIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h4" component="h1" fontWeight={700} color="primary.main">
                        Create New Program Template
                    </Typography>
                </Box>

                <Stack spacing={4}>
                    {/* Program Name */}
                    <TextField
                        label="Program Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        sx={textFieldStyles}
                        placeholder="e.g., Bricks Challenge"
                    />

                    {/* Age Range Section */}
                    <Box>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ color: '#673AB7', fontWeight: 600, mb: 2 }}>
                                Target Age Group
                            </FormLabel>
                            <RadioGroup
                                value={ageType}
                                onChange={handleAgeTypeChange}
                                row
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio sx={{ color: '#673AB7' }} />}
                                    label="Single Age"
                                />
                                <FormControlLabel
                                    value="multiple"
                                    control={<Radio sx={{ color: '#673AB7' }} />}
                                    label="Age Range"
                                />
                            </RadioGroup>
                        </FormControl>

                        <Box mt={2}>
                            {ageType === 'single' ? (
                                <TextField
                                    label="Age"
                                    type="number"
                                    value={singleAge}
                                    onChange={(e) => setSingleAge(e.target.value)}
                                    variant="outlined"
                                    inputProps={{ min: 1, max: 18 }}
                                    sx={{ width: 200, ...textFieldStyles }}
                                    placeholder="e.g., 8"
                                />
                            ) : (
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        label="From Age"
                                        type="number"
                                        value={ageFrom}
                                        onChange={(e) => setAgeFrom(e.target.value)}
                                        variant="outlined"
                                        inputProps={{ min: 1, max: 18 }}
                                        sx={{ width: 150, ...textFieldStyles }}
                                        placeholder="5"
                                    />
                                    <Typography>to</Typography>
                                    <TextField
                                        label="To Age"
                                        type="number"
                                        value={ageTo}
                                        onChange={(e) => setAgeTo(e.target.value)}
                                        variant="outlined"
                                        inputProps={{ min: 1, max: 18 }}
                                        sx={{ width: 150, ...textFieldStyles }}
                                        placeholder="8"
                                    />
                                </Stack>
                            )}
                        </Box>

                        {formData.ageRange.length > 0 && (
                            <Box mt={2}>
                                <Typography variant="subtitle2" gutterBottom>Selected Ages:</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {formData.ageRange.map((age, index) => (
                                        <Chip
                                            key={index}
                                            label={`${age.age} years`}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'primary.light',
                                                color: 'white',
                                                mb: 1
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/program-templates')}
                            sx={{
                                borderColor: '#673AB7',
                                color: '#673AB7',
                                borderRadius: 3,
                                px: 4,
                                fontWeight: 600,
                                '&:hover': {
                                    borderColor: '#5e35b1',
                                    backgroundColor: 'rgba(103, 58, 183, 0.04)'
                                }
                            }}
                        >
                            <CancelIcon sx={{ mr: 1 }} />
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || !formData.name}
                            sx={{
                                backgroundColor: '#673AB7',
                                borderRadius: 3,
                                px: 4,
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(103, 58, 183, 0.3)',
                                '&:hover': {
                                    backgroundColor: '#5e35b1',
                                    boxShadow: '0 6px 16px rgba(103, 58, 183, 0.4)'
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <SaveIcon sx={{ mr: 1 }} />
                                    Create Template
                                </>
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <ToastContainer />
        </Container>
    );
};

export default ProgramTemplateCreate;
